import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from './store'
import { useOrgStore } from './store'
import { API_BASE } from './env'

/**
 * Enterprise API Client - Axios Singleton with Smart Token Refresh
 * 
 * Features:
 * - Automatic Bearer token injection from Zustand store
 * - Organization ID header injection from Zustand store
 * - Concurrency-safe token refresh (mutex pattern)
 * - HttpOnly cookie support for refresh tokens
 */

// Create axios instance with global config
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Enable HttpOnly cookies
})

export interface ApiErrorPayload {
  error?: {
    code?: string
    message?: string
    status?: number
    retryable?: boolean
    retry_after?: number | null
    attempt_id?: number | string | null
    request_id?: string
  }
  code?: string
  message?: string
  detail?: unknown
  field_errors?: Record<string, unknown>
  eligibility?: EligibilityPayload
  [key: string]: unknown
}

export interface EligibilityPayload {
  allowed: boolean
  reason: string | null
  message: string
}

export interface ExtractedApiError {
  code: string
  message: string
  status?: number
  retryable: boolean
  retryAfter?: number | null
  attemptId?: number | string | null
  requestId?: string
  fieldErrors: Record<string, unknown>
  eligibility?: EligibilityPayload
}

function firstString(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    for (const item of value) {
      const result = firstString(item)
      if (result) return result
    }
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const result = firstString(item)
      if (result) return result
    }
  }
  return ''
}

function getResponseData(error: unknown): ApiErrorPayload | undefined {
  if (!axios.isAxiosError(error)) return undefined
  const data = error.response?.data
  if (!data || typeof data !== 'object') return undefined
  return data as ApiErrorPayload
}

export function extractApiError(error: unknown, fallback = 'Не вдалося виконати дію. Спробуйте ще раз.'): ExtractedApiError {
  const data = getResponseData(error)
  const envelope = data?.error
  const detailMessage =
    typeof data?.detail === 'string'
      ? data.detail
      : typeof data?.detail === 'object'
        ? firstString(data.detail)
        : ''
  const fieldErrors = (data?.field_errors || {}) as Record<string, unknown>
  const message =
    envelope?.message ||
    (typeof data?.message === 'string' ? data.message : '') ||
    detailMessage ||
    firstString(fieldErrors) ||
    fallback

  return {
    code: envelope?.code || (typeof data?.code === 'string' ? data.code : 'unknown_error'),
    message,
    status: envelope?.status || (axios.isAxiosError(error) ? error.response?.status : undefined),
    retryable: Boolean(envelope?.retryable),
    retryAfter: envelope?.retry_after,
    attemptId: envelope?.attempt_id,
    requestId: envelope?.request_id,
    fieldErrors,
    eligibility: data?.eligibility,
  }
}

export function getApiErrorMessage(error: unknown, fallback?: string): string {
  return extractApiError(error, fallback).message
}

// Mutex pattern for concurrent 401 handling
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: AxiosError) => void
}> = []

const AUTH_REFRESH_BYPASS_PATHS = [
  '/api/v1/auth/login/',
  '/api/v1/auth/register/',
  '/api/v1/auth/google/',
  '/api/v1/auth/verify-email/',
  '/api/v1/auth/resend-verification/',
  '/api/v1/auth/password-reset/request/',
  '/api/v1/auth/password-reset/confirm/',
  '/api/v1/auth/token/refresh/',
]

function shouldBypassRefresh(url?: string): boolean {
  if (!url) return false
  return AUTH_REFRESH_BYPASS_PATHS.some((path) => url.includes(path))
}

/**
 * Process all queued requests after token refresh
 */
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

/**
 * Request Interceptor: Inject tokens and headers
 */
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState()
    const { activeOrgId } = useOrgStore.getState()

    // Inject Authorization header if token exists
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    // Inject Organization ID header if active org exists
    if (activeOrgId) {
      config.headers['X-Organization-ID'] = activeOrgId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Response Interceptor: Handle 401 with concurrency-safe refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Only handle 401 errors
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    if (shouldBypassRefresh(originalRequest.url)) {
      return Promise.reject(error)
    }

    // Prevent infinite retry loop
    if (originalRequest._retry) {
      return Promise.reject(error)
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(originalRequest))
          },
          reject: (err: AxiosError) => {
            reject(err)
          },
        })
      })
    }

    // Mark request as retry and start refresh flow
    originalRequest._retry = true
    isRefreshing = true

    try {
      // Call refresh endpoint (relies on HttpOnly cookie)
      const response = await axios.post(
        `${API_BASE}/api/v1/auth/token/refresh/`,
        {},
        {
          withCredentials: true, // Send HttpOnly cookie
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      const newAccessToken = response.data.access

      // Update store with new access token
      useAuthStore.getState().setAccessToken(newAccessToken)

      // Process all queued requests with new token
      processQueue(null, newAccessToken)

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return apiClient(originalRequest)
    } catch (refreshError) {
      // Refresh failed - logout user
      processQueue(refreshError as AxiosError, null)
      useAuthStore.getState().logout()

      // Redirect to login
      window.location.href = '/login'

      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default apiClient


